import { useState } from 'react';
import { Loader2, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  classifyUploadError,
  type UploadCertificatePayload,
} from '@/features/certificates/api';
import type { Certificate } from '@/features/certificates/schemas';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: UploadCertificatePayload) => Promise<Certificate>;
  isSubmitting?: boolean;
}

const MAX_BYTES = 10 * 1024 * 1024;

export function CertificateUploadDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [result, setResult] = useState<Certificate | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = (): void => {
    setFile(null);
    setPassword('');
    setFileError(null);
    setPasswordError(null);
    setResult(null);
    setDragOver(false);
  };

  const handleClose = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFile = (f: File | null): void => {
    setFileError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setFileError('Arquivo maior que 10 MB.');
      setFile(null);
      return;
    }
    const name = f.name.toLowerCase();
    if (!name.endsWith('.pfx') && !name.endsWith('.p12')) {
      setFileError('Arquivo inválido — envie um .pfx ou .p12 válido.');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const submit = async (): Promise<void> => {
    setFileError(null);
    setPasswordError(null);
    if (!file) {
      setFileError('Selecione o arquivo do certificado.');
      return;
    }
    if (!password) {
      setPasswordError('Informe a senha do certificado.');
      return;
    }
    try {
      const data = await onSubmit({ file, password });
      setResult(data);
      setPassword('');
    } catch (err) {
      const classified = classifyUploadError(err);
      if (classified.kind === 'password') setPasswordError(classified.message);
      else setFileError(classified.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Enviar certificado A1</DialogTitle>
          <DialogDescription>
            Arraste o arquivo .pfx/.p12 ou selecione. A senha não fica em claro — o
            certificado é criptografado em repouso.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div
            data-testid="cert-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              handleFile(e.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              'mb-4 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-sm transition-colors',
              dragOver
                ? 'border-(--color-accent) bg-(--color-accent)/5'
                : 'border-(--color-border)',
            )}
          >
            <UploadCloud className="h-8 w-8 text-(--color-fg-muted)" aria-hidden />
            <p className="text-center text-(--color-fg-muted)">
              Arraste o arquivo ou
            </p>
            <label className="cursor-pointer rounded-md border border-(--color-border) bg-(--color-surface-elevated) px-3 py-1.5 text-sm font-medium hover:bg-(--color-border)">
              Escolher arquivo
              <input
                type="file"
                accept=".pfx,.p12,application/x-pkcs12"
                className="sr-only"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file ? (
              <p className="text-xs font-medium">{file.name}</p>
            ) : null}
          </div>

          {fileError ? (
            <p role="alert" className="mb-3 text-sm text-(--color-error)">
              {fileError}
            </p>
          ) : null}

          <FormField
            id="cert-password"
            label="Senha"
            error={passwordError ?? undefined}
            required
          >
            <Input
              id="cert-password"
              type="password"
              autoComplete="off"
              value={password}
              invalid={!!passwordError}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>

          {isSubmitting ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-(--color-fg-muted)">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Validando certificado…
            </div>
          ) : null}

          {result ? (
            <div
              role="status"
              className="mt-4 rounded-md border border-(--color-success) bg-(--color-surface) p-4 text-sm"
            >
              <p className="font-medium text-(--color-success)">
                Certificado válido
              </p>
              <dl className="mt-2 grid grid-cols-3 gap-1 text-xs">
                <dt className="col-span-1 text-(--color-fg-muted)">Titular</dt>
                <dd className="col-span-2">{result.holderName}</dd>
                <dt className="col-span-1 text-(--color-fg-muted)">CNPJ</dt>
                <dd className="col-span-2">{result.holderCnpj}</dd>
                <dt className="col-span-1 text-(--color-fg-muted)">Expira em</dt>
                <dd className="col-span-2">
                  {new Date(result.expiresAt).toLocaleDateString('pt-BR')}
                </dd>
              </dl>
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleClose(false)}
            disabled={isSubmitting}
          >
            {result ? 'Fechar' : 'Cancelar'}
          </Button>
          {!result ? (
            <Button onClick={submit} disabled={isSubmitting || !file}>
              {isSubmitting ? 'Validando…' : 'Validar e salvar'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
